import { Component, Inject, Injectable, OnInit, Optional } from '@angular/core'
import {
   API_BASE_URL,
   UserReviewClient,
   AiTemplateQuestion,
   AiTemplateSection,
   GenerateAiInsightsRequest,
   AiInsightDto,
} from 'src/app/shared/services/ppv.api'
import { DVM_REVIEW_TEMPLATE } from '../../dvm-tool-plan/review-template-dvm'
import { HM_REVIEW_TEMPLATE } from '../../dvm-tool-plan/review-template-hm'
import { RM_REVIEW_TEMPLATE } from '../../dvm-tool-plan/review-template-rm'
import { COS_REVIEW_TEMPLATE } from '../../dvm-tool-plan/review-template-cos'
import { DO_REVIEW_TEMPLATE } from '../../dvm-tool-plan/review-template-do'
import { CLINICAL_REVIEW_TEMPLATE } from '../../dvm-tool-plan/review-template-clinical'
import { CROSS_TRAINED_REVIEW_TEMPLATE } from '../../dvm-tool-plan/review-template-cross-trained'
import { RECEPTION_REVIEW_TEMPLATE } from '../../dvm-tool-plan/review-template-reception'
import { ReviewTemplate } from '../../dvm-tool-plan/components/review-assessment-sections/review-assessment-sections.component'
import { AuthManager } from 'src/app/shared/auth/AuthManager'

// ─── Shape of the JSON the AI returns (stored in insightsJson) ───────────────

export type SectionTone = 'mostly_positive' | 'leaning_positive' | 'mixed' | 'leaning_negative' | 'mostly_negative'

export interface AiSectionTheme {
   title: string
   frequency: string // plain phrase, e.g. "Raised by about 1 in 4"
   detail: string
   quote?: string
   quoteSource?: string
}

export interface AiSectionInsight {
   sectionId: string
   sectionTitle: string
   tone: SectionTone
   oneLine: string
   summary: string
   working: AiSectionTheme[]
   struggles: AiSectionTheme[]
}

export interface AiInsightsResult {
   executiveSummary: string
   overallTone: 'Positive' | 'Mixed' | 'Negative'
   sections: AiSectionInsight[]
}

// ─── Component ───────────────────────────────────────────────────────────────

interface ReviewTypeOption {
   key: string
   label: string
   color: string
   colorBg: string
}

@Component({
   selector: 'app-ai-insights',
   templateUrl: './ai-insights.component.html',
   styleUrls: ['./ai-insights.component.scss'],
   standalone: false,
})
export class AiInsightsComponent implements OnInit {
   // Dropdown options. "Team Member" combines the three team-level reviews.
   readonly reviewTypeOptions: ReviewTypeOption[] = [
      { key: 'DVM_Q1', label: 'aDVM Q1', color: '#03af9c', colorBg: '#14a19a' },
      { key: 'DVM_Q2', label: 'aDVM Q2', color: '#03af9c', colorBg: '#14a19a' },
	  { key: 'DVM_Q3', label: 'aDVM Q3', color: '#03af9c', colorBg: '#14a19a' },
      { key: 'HM', label: 'HM', color: '#0374b6', colorBg: '#0374b6' },
      { key: 'COS', label: 'COS', color: '#831183', colorBg: '#831183' },
      { key: 'RM', label: 'RM', color: '#fdb815', colorBg: '#fdb813' },
      { key: 'DO', label: 'DO', color: '#ee8b23', colorBg: '#ee8b23' },
      { key: 'TEAM', label: 'Team Member', color: '#e8603c', colorBg: '#e04721' },
   ]

   selectedKey = 'TEAM'

   loading = false
   generating = false
   error: string | null = null

   record: AiInsightDto | null = null
   result: AiInsightsResult | null = null

   expandedSections = new Set<string>()

   loggedInUser = ''

   constructor(
      private userReviewClient: UserReviewClient,
      private authMgr: AuthManager
   ) {
      this.authMgr.getCurrentUser().subscribe((user) => (this.loggedInUser = user.displayName))
   }

   ngOnInit(): void {
      this.load()
   }

   // ─── Data loading ─────────────────────────────────────────────────────────

   get selectedOption(): ReviewTypeOption {
      return this.reviewTypeOptions.find((o) => o.key === this.selectedKey) ?? this.reviewTypeOptions[0]
   }

   onReviewTypeChange(): void {
      this.load()
   }

   load(): void {
      this.loading = true
      this.error = null
      this.record = null
      this.result = null
      this.expandedSections.clear()

      this.userReviewClient.getAiInsights(this.selectedKey).subscribe({
         next: (record) => {
            this.record = record ?? null
            this.result = record ? this.parseInsights(record.insightsJson) : null
            // Open the most negative section by default so the page isn't a wall of closed rows
            if (this.result?.sections?.length) {
               const worst = [...this.result.sections].sort((a, b) => this.toneRank(b.tone) - this.toneRank(a.tone))[0]
               if (this.toneRank(worst.tone) >= 3) this.expandedSections.add(worst.sectionId)
            }
            this.loading = false
         },
         error: () => {
            this.error = 'Failed to load AI insights. Please try again.'
            this.loading = false
         },
      })
   }

   generate(): void {
      if (this.generating) return
      if (this.record && !confirm('Re-running the analysis will replace the current results for this review type. Continue?')) return

      this.generating = true
      this.error = null

      const request: GenerateAiInsightsRequest = {
         reviewTypeKey: this.selectedKey,
         sections: this.buildTemplateDigest(this.selectedKey),
      }

      this.userReviewClient.generateAiInsights(request).subscribe({
         next: (record) => {
            this.record = record
            this.result = this.parseInsights(record.insightsJson)
            this.expandedSections.clear()
            this.generating = false
         },
         error: (err) => {
            this.error =
               err?.status === 422
                  ? 'Not enough written answers have been submitted yet for this review type.'
                  : 'The analysis could not be generated. Please try again in a moment.'
            this.generating = false
         },
      })
   }

   private parseInsights(json: string): AiInsightsResult | null {
      try {
         const parsed = JSON.parse(json) as AiInsightsResult
         parsed.sections = (parsed.sections ?? []).map((s) => ({
            ...s,
            working: s.working ?? [],
            struggles: s.struggles ?? [],
         }))
         return parsed
      } catch {
         this.error = 'The saved analysis could not be read. Try re-running the analysis.'
         return null
      }
   }

   // ─── Template digest (what free-text questions exist, per section) ────────
   // The backend is template-agnostic: the client tells it which questions are
   // free text and what each section is called, straight from the same template
   // files the review forms use.

   private buildTemplateDigest(key: string): AiTemplateSection[] {
      if (key === 'TEAM') {
         return [
            ...this.digestTemplate(CLINICAL_REVIEW_TEMPLATE, 'Clinical · '),
            ...this.digestTemplate(CROSS_TRAINED_REVIEW_TEMPLATE, 'Cross-Trained · '),
            ...this.digestTemplate(RECEPTION_REVIEW_TEMPLATE, 'Reception · '),
         ]
      }
      const templates: Record<string, ReviewTemplate> = {
         DVM_Q1: DVM_REVIEW_TEMPLATE,
         DVM_Q2: DVM_REVIEW_TEMPLATE,
		 DVM_Q3: DVM_REVIEW_TEMPLATE,
         HM: HM_REVIEW_TEMPLATE,
         COS: COS_REVIEW_TEMPLATE,
         RM: RM_REVIEW_TEMPLATE,
         DO: DO_REVIEW_TEMPLATE,
      }
      return this.digestTemplate(templates[key], '')
   }

   private digestTemplate(template: ReviewTemplate, titlePrefix: string): AiTemplateSection[] {
      const sections: AiTemplateSection[] = []
      for (const section of template.sections) {
         const title = (section.revieweeTitle || section.reviewerTitle || '').trim()
         if (!title) continue
         if (/rating definitions|job description/i.test(title)) continue

         const questions: AiTemplateQuestion[] = []
         for (const q of section.questions as any[]) {
            if (q.type === 'static') continue
            const label = this.stripHtml(q.revieweeLabel || q.reviewerLabel || q.id)
            questions.push({
               id: q.id,
               label: label.length > 220 ? label.slice(0, 220) + '…' : label,
               isFreeText: q.type === 'textarea',
            })
         }
         if (questions.length === 0) continue
         const idPrefix = titlePrefix ? titlePrefix.replace(/[^a-z0-9]/gi, '_').toLowerCase() : ''
         sections.push({
            sectionId: idPrefix + section.id,
            title: titlePrefix + title,
            questions,
         })
      }
      return sections
   }

   get groupedSections() {
      const groups = [
         {
            type: 'Clinical Reviews',
            sections: this.result.sections.filter((s: any) => s.sectionTitle?.startsWith('Clinical')),
         },
         {
            type: 'Cross-Trained Reviews',
            sections: this.result.sections.filter((s: any) => s.sectionTitle?.startsWith('Cross-Trained')),
         },
         {
            type: 'Reception Reviews',
            sections: this.result.sections.filter((s: any) => s.sectionTitle?.startsWith('Reception')),
         },
      ]

      return groups.filter((group) => group.sections.length > 0)
   }

   private stripHtml(html: string): string {
      return (html || '')
         .replace(/<[^>]*>/g, ' ')
         .replace(/&nbsp;/g, ' ')
         .replace(/&amp;/g, '&')
         .replace(/\s+/g, ' ')
         .trim()
   }

   // ─── View helpers ─────────────────────────────────────────────────────────

   toneRank(tone: SectionTone): number {
      return { mostly_positive: 0, leaning_positive: 1, mixed: 2, leaning_negative: 3, mostly_negative: 4 }[tone] ?? 2
   }

   toneLabel(tone: SectionTone): string {
      return {
         mostly_positive: 'Mostly positive',
         leaning_positive: 'Leaning positive',
         mixed: 'Mixed',
         leaning_negative: 'Leaning negative',
         mostly_negative: 'Mostly concerns',
      }[tone]
   }

   toneClass(tone: SectionTone): string {
      return {
         mostly_positive: 'tone-pos-strong',
         leaning_positive: 'tone-pos-soft',
         mixed: 'tone-mixed',
         leaning_negative: 'tone-neg-soft',
         mostly_negative: 'tone-neg-strong',
      }[tone]
   }

   get positiveCount(): number {
      return this.result?.sections.filter((s) => this.toneRank(s.tone) <= 1).length ?? 0
   }

   get attentionCount(): number {
      return this.result?.sections.filter((s) => this.toneRank(s.tone) >= 3).length ?? 0
   }

   toggleSection(sectionId: string): void {
      if (this.expandedSections.has(sectionId)) this.expandedSections.delete(sectionId)
      else this.expandedSections.add(sectionId)
   }

   isExpanded(sectionId: string): boolean {
      return this.expandedSections.has(sectionId)
   }

   expandAll(): void {
      this.result?.sections.forEach((s) => this.expandedSections.add(s.sectionId))
   }

   jumpToSection(sectionId: string): void {
      this.expandedSections.add(sectionId)
      setTimeout(() => {
         document.getElementById('ai-section-' + sectionId)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      })
   }

   exportPdf(): void {
      // Expand everything so the printout is complete, then use the browser's
      // print-to-PDF. The SCSS contains @media print rules for a clean layout.
      this.expandAll()
      setTimeout(() => window.print(), 150)
   }

   trackBySectionId(_: number, s: AiSectionInsight): string {
      return s.sectionId
   }
}
